import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiGet, apiSend, entitlementState } from '../utils/api';
import { LockedFeaturePanel } from '../components/EntitlementStates';
import { BUYER_STATUSES, STATUS_META } from '../components/BuyerStatusBadge';
import { useAuth } from '../contexts/AuthContext';

// Editable fields the detail page manages (must match the PUT /api/buyers/:id allowlist).
const EDITABLE = [
  'name', 'company', 'email', 'phone',
  'buy_box_neighborhoods', 'buy_box_strategies', 'buy_box_max_price',
  'buy_box_min_beds', 'buy_box_min_sqft', 'buy_box_max_sqft',
  'past_purchases', 'total_volume', 'avg_close_days', 'proof_of_funds',
  'notes', 'status',
];

function toForm(b) {
  const f = {};
  for (const k of EDITABLE) f[k] = b[k] == null ? '' : String(b[k]);
  if (!f.status) f.status = 'buyer';
  return f;
}

function fmtDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return isNaN(d) ? '—' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function BuyerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { plan } = useAuth();

  const [buyer, setBuyer] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [locked, setLocked] = useState(null);
  const [error, setError] = useState(null);
  const [savedTick, setSavedTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiGet(`/api/buyers/${id}`)
      .then(b => { if (!alive) return; setBuyer(b); setForm(toForm(b)); setLoading(false); })
      .catch(err => {
        if (!alive) return;
        const s = entitlementState(err);
        if (s?.kind === 'locked') setLocked(s.error);
        else if (/404|not found/i.test(err.message)) setNotFound(true);
        else setError(err.message);
        setLoading(false);
      });
    return () => { alive = false; };
  }, [id]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const dirty = useMemo(() => {
    if (!buyer || !form) return false;
    return EDITABLE.some(k => String(buyer[k] == null ? '' : buyer[k]) !== String(form[k] ?? ''));
  }, [buyer, form]);

  async function save() {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError(null);
    try {
      const payload = {};
      for (const k of EDITABLE) payload[k] = form[k];
      const { buyer: updated } = await apiSend(`/api/buyers/${id}`, { method: 'PUT', body: payload });
      setBuyer(updated);
      setForm(toForm(updated));
      setSavedTick(t => t + 1);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (locked) {
    return (
      <div>
        <BackLink />
        <LockedFeaturePanel feature={locked.feature || 'buyer_database'} plan={locked.plan || plan?.key} />
      </div>
    );
  }
  if (loading) return <div style={{ color: 'var(--gray-500)', padding: 40 }}>Loading…</div>;
  if (notFound) return (
    <div>
      <BackLink />
      <div style={{ padding: 40, color: 'var(--gray-500)' }}>Buyer not found. It may have been removed.</div>
    </div>
  );
  if (!form) return <div style={{ color: '#B85450', padding: 40 }}>{error || 'Failed to load buyer.'}</div>;

  return (
    <div style={{ maxWidth: 920, margin: '0 auto' }}>
      <BackLink />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: `hsl(${(buyer.id * 47) % 360}, 55%, 35%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#fff' }}>
            {(form.name || '?').charAt(0)}
          </div>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em' }}>{form.name || 'Unnamed buyer'}</h1>
            <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
              {form.company || 'No company'} · Added {fmtDate(buyer.created_at)}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {savedTick > 0 && !dirty && <span style={{ fontSize: 12, color: '#4A7C59', fontWeight: 600 }}>✓ Saved</span>}
          <button
            onClick={save}
            disabled={saving || !dirty || !form.name.trim()}
            style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving || !dirty ? 'not-allowed' : 'pointer', opacity: saving || !dirty || !form.name.trim() ? 0.55 : 1 }}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && <div style={{ marginBottom: 14, fontSize: 13, color: '#B85450' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Contact + LLC */}
        <Card title="Contact">
          <Field label="Full Name *"><input value={form.name} onChange={set('name')} style={inp} /></Field>
          <Field label="Company / LLC"><input value={form.company} onChange={set('company')} placeholder="Blackburn Capital Ventures, LLC" style={inp} /></Field>
          <Field label="Email"><input value={form.email} onChange={set('email')} placeholder="buyer@example.com" style={inp} /></Field>
          <Field label="Phone"><input value={form.phone} onChange={set('phone')} placeholder="(602) 555-0100" style={inp} /></Field>
          <Field label="Status">
            <select value={form.status} onChange={set('status')} style={inp}>
              {BUYER_STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
            </select>
          </Field>
        </Card>

        {/* Buy Box */}
        <Card title="Buy Box">
          <Field label="Target Areas / Neighborhoods"><input value={form.buy_box_neighborhoods} onChange={set('buy_box_neighborhoods')} placeholder="Gilbert, Mesa, Queen Creek, 85234" style={inp} /></Field>
          <Field label="Strategy"><input value={form.buy_box_strategies} onChange={set('buy_box_strategies')} placeholder="Fix & Flip, Buy & Hold" style={inp} /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Max Price"><input value={form.buy_box_max_price} onChange={set('buy_box_max_price')} placeholder="$450k" style={inp} /></Field>
            <Field label="Min Beds"><input value={form.buy_box_min_beds} onChange={set('buy_box_min_beds')} placeholder="3" style={inp} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Min Sqft"><input value={form.buy_box_min_sqft} onChange={set('buy_box_min_sqft')} placeholder="1200" style={inp} /></Field>
            <Field label="Max Sqft"><input value={form.buy_box_max_sqft} onChange={set('buy_box_max_sqft')} placeholder="3000" style={inp} /></Field>
          </div>
        </Card>

        {/* Track record */}
        <Card title="Track Record">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Past Purchases"><input value={form.past_purchases} onChange={set('past_purchases')} placeholder="0" style={inp} /></Field>
            <Field label="Total Volume"><input value={form.total_volume} onChange={set('total_volume')} placeholder="$2.5M" style={inp} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Avg Close (days)"><input value={form.avg_close_days} onChange={set('avg_close_days')} placeholder="21" style={inp} /></Field>
            <Field label="Proof of Funds"><input value={form.proof_of_funds} onChange={set('proof_of_funds')} placeholder="$500k" style={inp} /></Field>
          </div>
        </Card>

        {/* Notes */}
        <Card title="Notes">
          <textarea value={form.notes} onChange={set('notes')} rows={9} placeholder="Buy criteria, lead temperature, financing, anything worth remembering…" style={{ ...inp, resize: 'vertical', fontFamily: 'inherit', minHeight: 180 }} />
        </Card>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        {form.email && <a href={`mailto:${form.email}`} style={btn('var(--teal)')}>Email Buyer</a>}
        {form.phone && <a href={`tel:${form.phone}`} style={btn('var(--gold)')}>Call {form.phone}</a>}
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/buyers" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--gray-500)', textDecoration: 'none', marginBottom: 16 }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
      Back to Buyers
    </Link>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 5 }}>{label}</span>
      {children}
    </label>
  );
}

const inp = {
  width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)',
  fontSize: 13, outline: 'none',
};

function btn(color) {
  return { padding: '9px 16px', borderRadius: 8, border: `1px solid ${color}55`, background: `${color}18`, color, fontSize: 13, fontWeight: 600, textDecoration: 'none' };
}
